import { INDUSTRIES, missionOf, type ControlStatus } from '@cyberscore/contracts';
import { Check, Circle, CircleDashed, HelpCircle, ShieldHalf, TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { href } from '../../lib/router';
import { Ring } from './charts';
import { useBusiness, useBusinessScore } from './context';
import { MissionIcon, controlLink } from './parts';
import { PROVENANCE_LABEL, controlQuestion, frameworks } from './progress';
import { NoAssessment, ScoreProblem } from './states';

const STATUS: Record<ControlStatus, { label: string; tone: string; Icon: typeof Check }> = {
  in_place: { label: 'In place', tone: 'ok', Icon: Check },
  partial: { label: 'Partly in place', tone: 'warn', Icon: CircleDashed },
  missing: { label: 'Missing', tone: 'bad', Icon: TriangleAlert },
  unknown: { label: 'Not sure', tone: 'no', Icon: HelpCircle },
  unanswered: { label: 'Not answered yet', tone: 'no', Icon: Circle },
};

/**
 * Missions (decision 0030), as the prototype lays them out: every mission on the left, the picked one's
 * protections on the right with their status, how each is known, and its points.
 */
export function MissionsPage({ mission }: { mission: string | null }) {
  const { loading } = useBusiness();
  const { current: a, view, derived } = useBusinessScore();
  const [mapping, setMapping] = useState(false);
  if (loading) return <div className="empty">Loading…</div>;
  if (!a) return <NoAssessment />;
  if (view.isPending) return <div className="empty">Loading…</div>;
  if (view.isError || !derived) return <ScoreProblem a={a} error={view.error} />;

  const trace = view.data.explanation;
  const { missions, points } = derived;
  const m = missions.find((x) => x.id === mission) ?? missions[0]!;
  const pctOf = (x: typeof m) => Math.round((100 * x.in_place) / x.controls);
  const pct = pctOf(m);
  const order: Record<ControlStatus, number> = { missing: 0, unanswered: 1, unknown: 2, partial: 3, in_place: 4 };
  const controls = trace.controls
    .filter((c) => missionOf(c.control)?.id === m.id)
    .sort((x, y) => (points[y.control] ?? 0) - (points[x.control] ?? 0) || order[x.status] - order[y.status] || (x.control < y.control ? -1 : 1));
  const avail = m.points || m.after_cap || 0;

  return (
    <>
      <div className="bpage-head">
        <div>
          <h1>Missions</h1>
          <p>Friendly goals instead of audit domains. Each one groups the protections behind it, and its points are for doing them together.</p>
        </div>
        <div className="head-actions">
          <label className="bchip">
            <input type="checkbox" checked={mapping} onChange={(e) => setMapping(e.target.checked)} /> Show framework mapping
          </label>
        </div>
      </div>
      <div className="bgrid g-side-l">
        <div className="stack-s">
          {missions.map((x) => {
            const p = pctOf(x);
            return (
              <a key={x.id} className={`mission${x.id === m.id ? ' on' : ''}`} href={href.missions(x.id)} aria-current={x.id === m.id ? 'true' : undefined}>
                <span className="top">
                  <span className="hicon"><MissionIcon id={x.id} /></span>
                  <span className="grow">
                    <h4>{x.name}</h4>
                    {x.pack && <span className="ind">{INDUSTRIES[x.pack]}</span>}
                  </span>
                  <span className="pct num">{p}%</span>
                </span>
                <span className={`bar thin ${p >= 75 ? 'ok' : p < 45 ? 'warn' : ''}`}><span style={{ width: `${p}%` }} /></span>
                <span className="row-between small">
                  <span className="muted">{x.in_place} of {x.controls} protections</span>
                  <span className="avail">{x.points ? `+${x.points} pts` : x.after_cap ? `+${x.after_cap} after cap` : x.next.steps.length ? '' : 'Done'}</span>
                </span>
              </a>
            );
          })}
        </div>
        <section className="bcard">
          <div className="mission-head">
            <span className="hicon big"><MissionIcon id={m.id} size={22} /></span>
            <div className="grow">
              <h2>{m.name}</h2>
              <p className="muted mt-s">{m.blurb}</p>
              <div className="row wrap gap-s mt">
                <span className="bbadge pri">{m.in_place} of {m.controls} protections in place</span>
                {avail > 0 && <span className="bbadge ok">+{avail} points {m.points ? 'available' : 'once the cap is lifted'}</span>}
                {m.lifts_cap && <span className="bbadge bad">Lifts the cap</span>}
                {m.pack && <span className="bbadge acc">{INDUSTRIES[m.pack]}</span>}
              </div>
            </div>
            <div className="ring-col">
              <Ring value={pct} color={pct >= 75 ? 'var(--ok-strong)' : 'var(--primary)'} />
              <span className="muted small">in place</span>
            </div>
          </div>
          <div className="bnote pri mt">
            <ShieldHalf size={15} />
            <span>
              {m.next.steps.length
                ? <>Each protection below adds a few points. The next {m.next.steps.length === 1 ? 'step adds' : `${m.next.steps.length} together add`} <b className="num">{m.next.points || '—'}</b>{m.points > m.next.points && <>, and finishing the whole mission adds <b className="num">{m.points}</b></>}.{!m.points && m.after_cap ? ' A missing protection elsewhere caps your score, so these count once it is fixed.' : ''}</>
                : 'Every protection in this mission is in place, or has nothing left to add.'}
            </span>
          </div>
          <hr className="divider" />
          <div>
            {controls.map((c) => {
              const st = STATUS[c.status];
              const pts = points[c.control] ?? 0;
              const link = controlLink(a, trace, c.control, m.id);
              const conf = c.status === 'unanswered' ? null : Math.round(c.confidence * 100);
              return (
                <div key={c.control} className="ctl">
                  <span className={`st ${st.tone}`}><st.Icon size={14} /></span>
                  <div className="grow">
                    <div className="t">{c.name}</div>
                    <div className="d">{controlQuestion(c.control) ?? st.label}</div>
                    <div className="row wrap gap-s mt-s">
                      <span className={`bbadge nodot ${c.status === 'in_place' ? 'ok' : c.status === 'missing' ? 'bad' : c.status === 'partial' ? 'warn' : ''}`}>{st.label}</span>
                      {conf !== null && c.provenance && <span className={`bbadge nodot ${conf >= 75 ? 'ok' : conf >= 40 ? 'pri' : ''}`}>{PROVENANCE_LABEL[c.provenance]} · {conf}%</span>}
                    </div>
                    {mapping && (
                      <div className="maps">
                        <span className="tag mono">{c.control}</span>
                        {frameworks(c.control).map((f) => <span key={f} className={`tag ${f === 'Kill switch' ? 'bad' : 'pri'}`}>{f}</span>)}
                      </div>
                    )}
                  </div>
                  <div className="ctl-end">
                    {pts > 0 ? (
                      <>
                        <div className="num ok-t strong">+{pts}</div>
                        {link.label !== 'See mission' && <a className="bbtn xs pri mt-s" href={link.to}>{link.label}</a>}
                      </>
                    ) : (
                      <span className="faint small">{c.status === 'in_place' ? 'In place' : ''}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      </div>
    </>
  );
} // prettier-ignore
